// One person's access — BR-37. A list of assignments, each a role plus a scope; one by default,
// "Agregar otra" for someone who is, say, Supervisor of one client and Auditor of another.
//
// Saving replaces the whole list (`users/assignments`), conditional on the revision this dialog
// loaded: if someone else saved this person's access meanwhile the backend answers 409
// `ACCESS_STALE`, and the dialog offers to reload rather than overwrite their change.
//
// Only grantable roles are offered (`rolesOtorgables`), and the scope picker offers only grantable
// clients, regions and offices. `problemasDeBorrador` repeats the backend's checks before the
// request, so Guardar is disabled with the reason in words instead of earning a 403; if the two
// ever disagree, the server's own sentence is shown.
import { useCallback, useMemo, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from "@mantis-sas/ui";

import { listCierreOffices } from "../../../ApiCalls/mantisBusiness";
import { setUserAssignments } from "../../../ApiCalls/mantisAccess";
import { conNombresDePermiso, descripcionDeRol, nombreDeRol } from "../../../utils/accesoDiccionario";
import {
  aAsignacionDelServidor,
  aBorrador,
  borradorNuevo,
  mapaDeRegiones,
  nombreDeUsuario,
  problemasDeBorrador,
  rolesOtorgables,
} from "../../../utils/cierreAcceso";
import { AccesoSelectorAlcance } from "./AccesoSelectorAlcance";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

const AVISO = "rounded-[8px] border px-2.5 py-1.5 text-[12.5px]";
const AVISO_ERROR = `${AVISO} border-brinks-danger-border bg-brinks-danger-soft text-brinks-danger`;
const AVISO_ATENCION = `${AVISO} border-brinks-warning-border bg-brinks-warning-soft text-brinks-warning`;

// When the role becomes Cliente, a scope it cannot hold is cut to what it can: never "all clients",
// and one client — the first already chosen — rather than a list the save would refuse.
const ajustarAlRol = (borrador, rol) => {
  if (!rol || !rol.clientBound) return borrador;
  if (borrador.scope.all) return { ...borrador, scope: { all: false, clients: [] } };
  if (borrador.scope.clients.length > 1) {
    return { ...borrador, scope: { all: false, clients: [borrador.scope.clients[0]] } };
  }
  return borrador;
};

function Asignacion({ indice, borrador, me, roles, clientes, oficinasPorCliente, cargarOficinas, problemas, onCambiar, onQuitar }) {
  const ofrecidos = rolesOtorgables(me, roles, clientes, borrador.role);
  const rol = roles.find((r) => r.id === borrador.role) || null;

  return (
    <section
      {...guia("Asignación", "Un rol y dónde aplica: todos los clientes, un cliente, una región u oficinas. Solo se ofrece lo que tú puedes otorgar. \"Quitar\" la elimina.")}
      aria-label={`Asignación ${indice + 1}`}
      className="space-y-3 rounded-[10px] border border-brinks-border bg-card p-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <label htmlFor={`${borrador.clave}-rol`} className="text-[12.5px] font-semibold text-brinks-foreground">
            Rol
          </label>
          <Select
            value={borrador.role}
            onValueChange={(role) => onCambiar(ajustarAlRol({ ...borrador, role }, roles.find((r) => r.id === role)))}
          >
            <SelectTrigger id={`${borrador.clave}-rol`} aria-label="Rol" className="bg-card text-brinks-foreground">
              <SelectValue placeholder={ofrecidos.length ? "Elige un rol" : "No puedes otorgar ningún rol"} />
            </SelectTrigger>
            <SelectContent className="brinks-scope font-redhat">
              {ofrecidos.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {nombreDeRol(r.id, roles)}
                  {r.builtIn ? "" : " (propio)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {rol ? <p className="text-[11.5px] text-brinks-muted-foreground">{descripcionDeRol(rol)}</p> : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 gap-1 text-brinks-danger"
          aria-label={`Quitar asignación ${indice + 1}`}
          onClick={onQuitar}
        >
          <Trash2 className="h-4 w-4" />
          Quitar
        </Button>
      </div>

      <AccesoSelectorAlcance
        idBase={borrador.clave}
        me={me}
        rol={rol}
        clientes={clientes}
        oficinasPorCliente={oficinasPorCliente}
        cargarOficinas={cargarOficinas}
        valor={borrador.scope}
        onChange={(scope) => onCambiar({ ...borrador, scope })}
      />

      {problemas.length > 0 ? (
        <ul className={`${AVISO_ATENCION} list-disc space-y-0.5 pl-6`}>
          {problemas.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function Contenido({ token, mantisBusinessId, usuario, me, roles, clientes, onGuardado, onRecargar, onCerrar }) {
  const [borradores, setBorradores] = useState(() => (usuario.assignments || []).map(aBorrador));
  const [oficinasPorCliente, setOficinasPorCliente] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  // One request per client for the dialog's life, however many assignments ask for it.
  const pedidas = useRef(new Set());
  const cargarOficinas = useCallback(
    async (code) => {
      if (!code || pedidas.current.has(code)) return;
      pedidas.current.add(code);
      const response = await listCierreOffices(token, mantisBusinessId, code).catch(() => null);
      const oficinas = response?.success ? response.data.offices || [] : [];
      if (!response?.success) {
        setError({ message: `No se pudieron cargar las oficinas de ${code}.` });
      }
      setOficinasPorCliente((actual) => ({ ...actual, [code]: oficinas }));
    },
    [token, mantisBusinessId]
  );

  const regionDe = useMemo(() => mapaDeRegiones(oficinasPorCliente), [oficinasPorCliente]);
  const nombreDeCliente = useCallback((code) => clientes.find((c) => c.code === code)?.name || code, [clientes]);
  const problemas = borradores.map((b) => problemasDeBorrador(b, { me, roles, regionDe, nombreDeCliente }));
  const hayProblemas = problemas.some((p) => p.length > 0);

  const cambiar = (clave, borrador) => setBorradores((actual) => actual.map((b) => (b.clave === clave ? borrador : b)));
  const quitar = (clave) => setBorradores((actual) => actual.filter((b) => b.clave !== clave));
  const agregar = () => {
    const ofrecidos = rolesOtorgables(me, roles, clientes);
    setBorradores((actual) => [...actual, borradorNuevo(ofrecidos.length === 1 ? ofrecidos[0].id : "")]);
  };

  const guardar = async () => {
    setEnviando(true);
    setError(null);
    const response = await setUserAssignments(
      token,
      usuario.id,
      borradores.map(aAsignacionDelServidor),
      usuario.revision
    ).catch(() => null);
    setEnviando(false);
    if (!response?.success) {
      setError({
        message: conNombresDePermiso(response?.message) || "No se pudo guardar el acceso. Inténtalo de nuevo.",
        stale: response?.errorCode === "ACCESS_STALE",
      });
      return;
    }
    onGuardado(response.data);
  };

  return (
    <>
      <div className="max-h-[65vh] space-y-3 overflow-y-auto p-4">
        {borradores.length === 0 ? (
          <p className={AVISO_ATENCION}>
            Sin asignaciones, {nombreDeUsuario(usuario)} no verá ningún cliente cuando el acceso sea obligatorio.
          </p>
        ) : (
          borradores.map((borrador, indice) => (
            <Asignacion
              key={borrador.clave}
              indice={indice}
              borrador={borrador}
              me={me}
              roles={roles}
              clientes={clientes}
              oficinasPorCliente={oficinasPorCliente}
              cargarOficinas={cargarOficinas}
              problemas={problemas[indice]}
              onCambiar={(b) => cambiar(borrador.clave, b)}
              onQuitar={() => quitar(borrador.clave)}
            />
          ))
        )}

        <Button {...guia("Agregar asignación", "Suma otro rol en otro alcance a la misma persona.")} type="button" variant="outline" size="sm" className="gap-1.5" onClick={agregar}>
          <Plus className="h-4 w-4" />
          {borradores.length === 0 ? "Agregar asignación" : "Agregar otra asignación"}
        </Button>

        {error ? (
          <div role="alert" className={AVISO_ERROR}>
            <p>{error.message}</p>
            {error.stale ? (
              <Button type="button" variant="outline" size="sm" className="mt-1.5 h-7" onClick={onRecargar}>
                Recargar el acceso actual
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <DialogFooter className="gap-2 border-t p-3">
        <Button type="button" variant="outline" onClick={onCerrar} disabled={enviando}>
          Cancelar
        </Button>
        <Button {...guia("Guardar", "Guarda todas las asignaciones juntas. Si otra persona las cambió mientras tanto, te pide recargar en vez de sobrescribir.")} type="button" onClick={guardar} disabled={enviando || hayProblemas}>
          {enviando ? <Spinner className="mr-1.5 h-4 w-4" /> : null}
          Guardar acceso
        </Button>
      </DialogFooter>
    </>
  );
}

export function AccesoAsignaciones({ open, onOpenChange, token, mantisBusinessId, usuario, me, roles, clientes, onGuardado, onRecargar }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="brinks-scope font-redhat max-h-[92vh] max-w-2xl gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b p-4 text-left">
          <div className="flex min-w-0 items-center gap-1">
            <DialogTitle className="text-[16px] font-semibold">
              Acceso de {usuario ? nombreDeUsuario(usuario) : ""}
            </DialogTitle>
            <BotonGuia guiaId="dialogo-acceso-usuario" titulo="Acceso de una persona" texto="Cada asignación es un rol y dónde aplica. Una persona puede tener varias: lo que puede hacer es la suma de todas." nombre="Acceso" />
          </div>
          <p className="text-[12.5px] text-brinks-muted-foreground">
            Cada asignación es un rol y dónde aplica. Una persona puede tener varias: lo que puede hacer es la suma de todas.
          </p>
        </DialogHeader>
        {open && usuario ? (
          // Keyed by revision: reloading after a 409 starts again from what is stored now.
          <Contenido
            key={`${usuario.id}-${usuario.revision}`}
            token={token}
            mantisBusinessId={mantisBusinessId}
            usuario={usuario}
            me={me}
            roles={roles}
            clientes={clientes}
            onGuardado={onGuardado}
            onRecargar={onRecargar}
            onCerrar={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
